'use client';

import { useState } from 'react';
import { clientChoiceDetail, IMPORTED_TAG, type ClientChoicesBlock } from '@/lib/chat/blocks/client-choices';

/**
 * "Which Customer K?" — the clients a name matched, each with what tells them
 * apart: postcode, whether they came from the old system, and their last job.
 *
 * A row ANSWERS her, so it is bare (no chevron — BubbleBlock's rule 2) and
 * quiet (no orange: picking a customer sends nothing and changes nothing). The
 * tap collapses the list to the one chosen, so the transcript keeps a record of
 * which, and sends the row's server-built reply as the owner's own words. What
 * happens next goes through the ordinary path with all of its checks.
 *
 * Nothing here computes a fact: every word on a row came off the block.
 */
export function ClientChoices({ block, live, onAnswer }: {
  block: ClientChoicesBlock;
  live: boolean;
  onAnswer: (text: string) => void;
  onOpen: (path: string) => void;
}) {
  const [chosen, setChosen] = useState<string | null>(null);
  const pick = (id: string, reply: string) => {
    if (!live || chosen) return;
    setChosen(id);
    onAnswer(reply);
  };
  const total = block.rows.length + block.more;
  const showAll = chosen === null;

  return (
    <div className="mchat-which" role="group" aria-label={`${total} matching customers`}>
      {block.rows.filter(r => showAll || r.clientId === chosen).map(row => {
        const detail = clientChoiceDetail(row);
        const on = row.clientId === chosen;
        return (
          <button
            key={row.clientId}
            type="button"
            className={`mchat-which-row${on ? ' mchat-which-row--on' : ''}`}
            aria-pressed={on}
            disabled={!live || chosen !== null}
            onClick={() => pick(row.clientId, row.reply)}
          >
            <span className="mchat-which-title">
              <span className="mchat-which-name">{row.name}</span>
              {row.postcode && <span className="mchat-which-postcode">{row.postcode}</span>}
              {row.imported && <span className="mchat-which-tag">{IMPORTED_TAG}</span>}
            </span>
            {detail && <span className="mchat-which-detail">{detail}</span>}
            {!row.postcode && !detail && <span className="mchat-which-detail">No postcode on file</span>}
          </button>
        );
      })}
      {block.more > 0 && showAll && (
        <p className="mchat-which-more">
          {block.more === 1 ? '1 more with that name is not shown.' : `${block.more} more with that name are not shown.`}
        </p>
      )}
      {(showAll || chosen === 'none') && (
        <button
          type="button"
          className={`mchat-which-row mchat-which-row--none${chosen === 'none' ? ' mchat-which-row--on' : ''}`}
          aria-pressed={chosen === 'none'}
          disabled={!live || chosen !== null}
          onClick={() => pick('none', block.none.reply)}
        >
          <span className="mchat-which-title"><span className="mchat-which-name">{block.none.label}</span></span>
        </button>
      )}
    </div>
  );
}
